<template>
    <div id="jimsTradeTrackerTable">
        <div class="mb-8 w-full">
            <div class="flex flex-wrap items-center mb-2 pb-4 border-b">
                <div v-if="currentCategory.name === 'Micro Positions'" class="mt-4">
                    <strong>As of Dec 30th Jim will no longer be actively trading this portfolio. This change does not make the Micro plan obsolete, you can still take the same trades through the regular CommodityTrends Trading Plan 2.0.</strong>
                </div>
                <div v-if="pasCategories.length > 0" class="flex flex-wrap items-center mt-2">
                    <label for="cCategory" class="uppercase font-bold mr-2 text-sm">Trade Tracker's Category:</label>
                    <div class="xs:w-full mt-2 sm:mt-0">
                        <select id="cCategory" @change="onChangeCategory" v-model="currentCategory" class="form-control form-control-sm form-control-inline align-bottom bg-white">
                            <template v-for="category in pasCategories" :key="category">
                            <option :value="category">{{ category.name }}</option>
                            </template>
                        </select>
                    </div>
                </div>
                <div class="inline-block mt-0 xs:mt-2 sm:mt-2 w-full md:w-auto ml-auto" v-if="pasData.totals">
                    <span class="uppercase font-bold mt-2 text-sm">Trade Tracker P/L</span><span class="uppercase mt-2 text-sm">{{ getTotalPL() }}</span>
                </div>
            </div>

            <div class="relative">
                <div v-show="loading" class="absolute z-50 w-full h-full bg-spinner-ghost flex flex-col items-center">
                    <spinner class="mb-1"></spinner>
                </div>
                <div v-if="pasData.totals">
                    <div class="mb-4 md:flex md:justify-between items-center print:hidden">
                        <div class="flex-auto">
                            <div class="flex flex-wrap">
                                <div class="text-xs sm:text-sm font-semibold mr-2">Go to:</div>
                                <template v-for="(pasGroupItems, key, index) in pasData.data" :key="key">
                                    <template v-if="pasGroupItems.length > 0">
                                        <a @click.prevent="scrollTo(`scrollTo-${index}`)" class="align-middle text-xs sm:text-sm pr-2 font-semibold cursor-pointer">{{ key }}</a>
                                    </template>
                                </template>
                            </div>
                        </div>
                        <div class="flex-auto text-right" v-if="haveJimsCharts()">
                            <button class="items-center text-xs lg:text-sm btn btn-sm text-blue-600 hover:text-blue-400 mb-2 mt-0 sm:mt-2" @click="openFlipcharts">
                                <span class="mr-1">FlipCharts</span> <i class="fas fa-chart-area"></i>
                            </button>
                        </div>
                    </div>
                    <div class="table-responsive relative border-t-2 border-blue-600">
                        <table ref="containerRef" class="table table-bordered jims-trade-tracker-table w-full">
                            <thead ref="theadRef">
                                <tr>
                                    <th style="width:3%">Qty</th>
                                    <th style="width:4%">Dir</th>
                                    <th style="width:20%">Pattern</th>
                                    <th style="width:5%">Strategy</th>
                                    <th style="width:5%">Entry&nbsp;Date</th>
                                    <th style="width:8%">Entry&nbsp;Price</th>
                                    <th style="width:8%">Current</th>
                                    <th style="width:5%">Change</th>
                                    <th style="width:5%">P/L</th>
                                    <th style="width:7%">Stop&nbsp;Loss</th>
                                    <th style="width:8%">Target</th>
                                    <th style="width:7%">Exit&nbsp;Date</th>
                                    <th style="width:7%">Exit&nbsp;Price</th>
                                    <th style="width:8%">Closed&nbsp;P/L</th>
                                </tr>
                            </thead>
                            <tbody>
                            <template v-for="(pasGroupItems, key, index) in pasData.data" :key="key">
                                <template v-if="pasGroupItems.length > 0">
                                    <tr class="bg-blue-200">
                                        <td colspan="14" class="jims-tt-item-category" :id="`scrollTo-${index}`">
                                            <span class="font-bold text-left text-white uppercase">
                                                {{ key }}
                                            </span>
                                        </td>
                                    </tr>
                                    <template v-for="pasItem in pasGroupItems" :key="pasItem.id">
                                    <tr class="bg-tan-200">
                                        <td colspan="14">
                                            <div class="flex flex-wrap w-full content-center items-center">
                                                <div class="inline-block self-center">
                                                    <span class="font-bold">
                                                        #{{ pasItem.id }} - {{ pasItem.title }}
                                                    </span>
                                                    <div class="inline-block ml-2 print:hidden" v-if="buttonOptions[pasItem.symbolType]">
                                                        <button-group
                                                            :jims-charts="jimsCharts"
                                                            :symbol="pasItem.raw.symbol"
                                                            :groups="buttonOptions[pasItem.symbolType]['value']"
                                                            :underlying-future="pasItem.underlyingFuture || null"
                                                        >
                                                        </button-group>
                                                    </div>
                                                </div>
                                                <div class="ml-auto" v-if="pasItem.expiration_date">
                                                    <span class="align-text-top">Expires: {{ pasItem.expiration_date }}</span>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr :class="{'bg-white': pasItem.exit_date, 'bg-yellow-200': !pasItem.exit_date}">
                                        <td>{{ pasItem.quantity || '-' }}</td>
                                        <td :class="getClassDirection(pasItem.direction)">{{ getDirection(pasItem.direction) }}</td>
                                        <td class="leading-tight jims-tt-item-category">{{ pasItem.pattern || '-' }}</td>
                                        <td>{{ pasItem.strategy || '-' }}</td>
                                        <td>{{ pasItem.entry_date || '-' }}</td>
                                        <td>{{ pasItem.formattedEntryPrice || '-' }}</td>
                                        <td>
                                            <template v-if="pasItem.closed_profit_loss || pasItem.exit_price">-</template>
                                            <template v-else>{{ pasItem.current_price || '-' }}</template>
                                        </td>
                                        <td v-if="pasItem.profit_loss" :class="getClass(pasItem.raw.priceChange)">{{ pasItem.priceChange || '-' }}</td><td v-else>-</td>
                                        <td :class="getClass(pasItem.profit_loss)" class="font-bold">{{ pasItem.profit_loss || '-' }}</td>
                                        <td>{{ pasItem.stop_loss || '-' }}</td>
                                        <td>{{ pasItem.target_price || '-' }}</td>
                                        <td>{{ pasItem.exit_date || '-' }}</td>
                                        <td>{{ pasItem.formattedExitPrice || '-' }}</td>
                                        <td :class="getClass(pasItem.closed_profit_loss)" class="font-bold">{{ pasItem.closed_profit_loss || '-' }}</td>
                                    </tr>
                                    <tr class="bg-white" v-if="hasTradeNotes(pasItem.notes)">
                                        <td colspan="14" class="jims-tt-item-notes">
                                            <div class="w-full flex items-center">
                                                <div class="inline-block">
                                                    <em>{{ getTradeNotes(pasItem.notes)[0]['description'] }}</em>
                                                </div>
                                                <div class="inline-block ml-auto print:hidden">
                                                    <div class="flex justify-end">
                                                        <div @click="tradeArchiveModal = pasItem" class="btn btn-xs btn-blue-ghost btn-shadow-none btn-notes-action"><strong>Trade&nbsp;Notes</strong></div>
                                                        <div v-if="pasItem.video_id" @click="openTradeVideoLink(pasItem)" class="btn btn-xs btn-blue-ghost btn-shadow-none btn-notes-action ml-1"><strong>Trade&nbsp;Video</strong></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                    </template>
                                </template>
                            </template>
                            </tbody>
                            <tfoot>
                                <tr class="border-b-2 border-blue-600">
                                    <th>Qty</th>
                                    <th>Dir</th>
                                    <th>Pattern</th>
                                    <th>Strategy</th>
                                    <th>Entry Date</th>
                                    <th>Entry Price</th>
                                    <th>Current</th>
                                    <th>Change</th>
                                    <th>P/L</th>
                                    <th>Stop Loss</th>
                                    <th>Target</th>
                                    <th>Exit Date</th>
                                    <th>Exit&nbsp;Price</th>
                                    <th>Closed P/L</th>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
                <alert-box v-else>
                    <template #body>
                        There are no CommodityTrends Daily Video Service trades data.
                    </template>
                </alert-box>
            </div>
        </div>

        <modal @close="tradeArchiveModal = null" v-if="tradeArchiveModal" className="max-w-sm w-full">
            <template #header>
                <div class="text-blue-600">
                    Trade #{{ tradeArchiveModal.id }}
                </div>
            </template>
            <template #body>
                <div class="pb-3">
                    <h4 class="text-yellow-500">{{ tradeArchiveModal.title }}</h4>
                    <div class="mt-3">
                        <table class="table table-hover table-striped border">
                            <tbody>
                                <tr v-for="(note, index) in getTradeNotes(tradeArchiveModal.notes)" :class="{ 'border-t-0': index > 0 }" :key="index">
                                    <td>
                                        <div class="w-full p-2">
                                            <p class="text-blue-500"><strong>{{ (new Date(note.date)).toLocaleDateString('en-US') }}</strong></p>
                                            <div class="leading-tight">
                                                {{ note.description }}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="flex justify-center">
                    <div class="btn btn-sm btn-yellow-ghost inline-flex mx-1" @click="tradeArchiveModal = null">Close</div>
                </div>
            </template>
        </modal>
    </div>
</template>

<script setup>
import { ref, computed, getCurrentInstance, onMounted, onUnmounted, onUpdated } from 'vue';
import { useStickyHeader } from '../DataTable/mixins/StickTableHeader';
import { useFlipcharts } from '../DataTable/mixins/FlipCharts';

const props = defineProps({
    initialPas: {
        type: Object,
        required: true,
    },
    pasCategories: {
        type: [Object, Array],
        required: true,
    },
    buttonOptions: Object,
    jimsCharts: {
        type: [Object, Array],
        default: () => [],
        required: false,
    },
});

const { containerRef, theadRef, handleScroll, resizeHeader } = useStickyHeader();
const { flipcharts } = useFlipcharts();

const { appContext } = getCurrentInstance();
const currentCategory = ref(props.pasCategories[0]);

const urlParams = new URLSearchParams(window.location.search);
if (urlParams.get('category')) {
    currentCategory.value = props.pasCategories.find((mCategory) => mCategory.id == urlParams.get('category'));
}

const pasData = ref(props.initialPas || []);
const loading = ref(false);
const tradeArchiveModal = ref(null);

const offset = computed(() => {
    const thead = document.querySelector('table.table thead');
    return thead ? thead.getBoundingClientRect().height * -1 : 0;
});

const scrollTo = (element) => {
    const options = {
        offset: offset.value,
        container: document.body,
    };
    appContext.app.config.globalProperties.$scrollTo(`#${element}`, undefined, options);
};

const openTradeVideoLink = (item) => {
    window.open(window.route('main.videos.show', item.video_id), '_blank');
};

const hasTradeNotes = (notes) => {
    return notes ? notes.some(n => n.public) : false;
};

const getTradeNotes = (notes) => {
    return notes ? notes.filter(n => n.public) : false;
};

const showMore = (event) => {
    var p = event.target.parentElement;
    if (p.classList.contains('jims-tt-text-overflow-dynamic-container-collapsed')) {
        event.target.innerHTML = 'Show More';
        p.classList.remove('jims-tt-text-overflow-dynamic-container-collapsed');
    } else {
        event.target.innerHTML = 'Show Less';
        p.classList.add('jims-tt-text-overflow-dynamic-container-collapsed');
    }
};

const getTotalPL = () => {
    if (Object.keys(pasData.value).length) {
        let totalPL = pasData.value.total_profit_loss;
        let strTotalPL = Number(totalPL.toFixed(2)).toLocaleString('en-US', { minimumFractionDigits: 2 });
        return ': ' + ((totalPL < 0) ? ('-$' + strTotalPL.replace(/-/, '')) : '$' + strTotalPL);
    }
    return '';
};

const onChangeCategory = () => {
    loading.value = true;
    const route = window.route('jims-desk.trade-tracker');
    axios.get(route, { params: { category: currentCategory.value.id } })
        .then((response) => {
            if (response.data && response.data.pasData && response.data.pasData.totals > 0) {
                pasData.value = response.data.pasData;
            } else {
                pasData.value = [];
            }
            loading.value = false;
        }).catch((error) => {
            console.error(error);
            pasData.value = [];
            loading.value = false;
        });
};

const getDirection = (value) => {
    if (value == 'U') {
        return 'Up';
    } else if (value == 'D') {
        return 'Down';
    } else {
        return 'Neutral';
    }
};

const getClassDirection = (value) => {
    if (value == 'U') {
        return 'text-up';
    } else if (value == 'D') {
        return 'text-down';
    } else {
        return 'text-unch';
    }
};

const getClass = (value) => {
    if (typeof (value) === 'string') {
        value = value.replace(/\$|,/g, '');
    }
    if (value > 0) {
        return 'text-up';
    } else if (value < 0) {
        return 'text-down';
    } else if (value == 0) {
        return 'text-unch';
    }
};

const haveJimsCharts = () => {
    let haveItems = false;
    if (pasData.value.totals > 0) {
        const chartIds = pasData.value.symbols.map(mSymbol => {
            let jc = props.jimsCharts.find(mChart => mChart.symbol === mSymbol);
            return (jc && jc.chart_id);
        }).filter(Boolean);
        haveItems = (chartIds.length);
    }
    return haveItems;
};

const openFlipcharts = () => {
    if (pasData.value.totals > 0) {
        const chartIds = pasData.value.symbols.map(mSymbol => {
            let jc = props.jimsCharts.find(mChart => mChart.symbol === mSymbol);
            return (jc && jc.chart_id);
        }).filter(Boolean);

        flipcharts(null, chartIds);
    }
};

const activateShowMore = () => {
    var elementList = document.getElementsByClassName("jims-tt-text-overflow-dynamic-ellipsis");
    for (var idx = 0; idx < elementList.length; idx++) {
        // reset the 'Show More' button
        elementList.item(idx).parentElement.classList.remove('jims-tt-text-overflow-dynamic-container-collapsed');
        elementList.item(idx).nextElementSibling.innerHTML = 'Show More';
        // show or hide button
        if (elementList.item(idx).offsetWidth < elementList.item(idx).scrollWidth) {
            elementList.item(idx).nextElementSibling.style.display = 'inline-flex';
        } else {
            elementList.item(idx).nextElementSibling.style.display = 'none';
        }
    }
};

const resizeJimsTT = () => {
    resizeHeader();
    activateShowMore();
};

onMounted(() => {
    activateShowMore();

    // Handled in mixins
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', resizeJimsTT);
});

onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll);
    window.removeEventListener('resize', resizeJimsTT);
});

onUpdated(() => {
    activateShowMore();
});
</script>

<style lang="scss">
#jimsTradeTrackerTable {
    .jims-tt-item-notes {
        @apply leading-tight;
    }

    .jims-tt-text-overflow-dynamic-container-collapsed {
        .jims-tt-text-overflow-dynamic-ellipsis {
            position: relative;
            white-space: normal;
        }
    }

    .jims-tt-text-overflow-dynamic-container {
        position: relative;
        max-width: 100%;
        padding: 0 !important;
        margin: 0.25rem auto;
        display: -webkit-flex;
        display: -moz-flex;
        display: flex;
        vertical-align: text-bottom !important;
    }
    .jims-tt-text-overflow-dynamic-ellipsis {
        position: absolute;
        white-space: nowrap;
        overflow-y: visible;
        overflow-x: hidden;
        text-overflow: ellipsis;
        -ms-text-overflow: ellipsis;
        -o-text-overflow: ellipsis;
        max-width: 100%;
        min-width: 0;
        width: 100%;
        top: 0;
        left: 0;
        text-align: left;
        padding-right: 5rem;
        z-index: 1;
    }
    .jims-tt-text-overflow-dynamic-container:after,
    .jims-tt-text-overflow-dynamic-ellipsis:after {
        content: '-';
        display: inline;
        visibility: hidden;
        width: 0;
    }

    .jims-tt-item-notes-more {
        @apply text-right inline-flex ml-auto cursor-pointer text-blue-500 text-xs py-px;
        z-index: 2;
        white-space: nowrap;
    }

    .jims-trade-tracker-table {
        &.table {
            thead,
            tfoot {
                td, th {
                    @apply px-2 py-1;
                }
            }
            thead {
                tr {
                    @apply border-t;
                    td, th {
                        @apply text-center;
                    }
                }
            }
            tbody {
                td, th {
                    @apply px-2;
                    &:not(.jims-tt-item-notes):not(.jims-tt-item-category) {
                        @apply text-center;
                    }
                }
            }
        }
    }
}
</style>
